import { useAtom } from 'jotai'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TransactionResult } from 'src/common/contracts/hooks/useExecuteTx'
import { AnyToken } from 'src/common/types/models/Tokens'
import NumericField from 'src/common/ui-kit/NumericField/NumericField'
import TokenIcon from 'src/common/ui-kit/TokenIcon/TokenIcon'
import { displayAmount } from 'src/common/utils/formatters/number'
import TransactionButton from '../../Buttons/TransactionButton'
import MaxMinInput from '../components/MaxMinInput'
import { viccCountAtom } from '../bundle-store'
import useCarbonApproval from './hooks/useCarbonApproval'
import useCarbonBundleDepositState from './hooks/useCarbonBundleDepositState'
import useCarbonBundleDepositTx from './hooks/useCarbonBundleDepositTx'
import useCarbon from 'src/common/queries/useCarbon'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import { useAllTokensForOwner } from 'src/common/queries/useTokensForOwner/useAllTokensForOwner'
import useLockedBalance from 'src/pages/WalletPage/hooks/useLockedBalance'
import useCarbonBundleInfo from 'src/common/contracts/hooks/carbon-bundles/useCarbonBundleInfo'

interface CarbonBundleDepositProps {
  onSuccess(tx: TransactionResult): void
}

const CarbonBundleDeposit: React.FC<CarbonBundleDepositProps> = ({
  onSuccess,
}) => {
  const { carbonBundleInfo, loading: bundleInfoLoading } = useCarbonBundleInfo()

  const { t } = useTranslation('carbonBundles', {
    keyPrefix: 'depositModal',
  })

  const [viccCount, setViccCount] = useAtom(viccCountAtom)
  const updateViccCount = (v: number) => {
    v = Math.floor(v)
    setViccCount(v)
  }

  const { carbon } = useCarbon()
  const {
    ownedCarbonTokens,
    receivedValue,
    loading: stateLoading,
    maxValue,
    vicc,
  } = useCarbonBundleDepositState()

  const { loading: approvalLoading, approveVicc: approveTx } =
    useCarbonApproval()

  const { depositTx } = useCarbonBundleDepositTx()

  const tx = approveTx ? [approveTx!, depositTx!] : [depositTx]

  const { smartAccountInfo } = useSmartAccount()
  const { refetch: lockedRefetch } = useLockedBalance()
  const { refetch: allTokensRefetch } = useAllTokensForOwner(
    smartAccountInfo?.address || ''
  )

  const handleSuccess = (tx: TransactionResult) => {
    onSuccess?.(tx)
    allTokensRefetch()
    lockedRefetch()
  }

  const noOwnedNFTs = !stateLoading && ownedCarbonTokens.length === 0

  const txButtonDisabled = viccCount === 0

  const txButtonCaption = t('actions.confirm')

  const txButtonLoading = approvalLoading || stateLoading

  return (
    <div className='flex flex-col'>
      <h2 className='modal-title'>{t('header')}</h2>
      <p className='modal-description'>{t('addNfts')}</p>

      <ul className='marked-list mb-2'>
        <li>{t('info.carbonPrice', { price: displayAmount(1) })}</li>
      </ul>

      <p className='modal-description'>{t('info.burn')}</p>
      <h3 className='modal-subtitle'>{t('add')}</h3>

      {noOwnedNFTs ? (
        <div className='flex flex-col items-center justify-center py-2 rounded'>
          <div className='modal-description text-lg font-semibold'>
            No assets in your wallet
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-2 justify-between mb-2'>
          <MaxMinInput
            onChange={updateViccCount}
            value={viccCount}
            maxValue={maxValue}
            token={vicc as AnyToken}
          />
        </div>
      )}

      <h3 className='modal-subtitle'>{t('receive')}</h3>
      <NumericField
        value={receivedValue}
        StartAdornment={carbon && <TokenIcon token={carbon} />}
        EndAdornment={
          <span className='modal-description'>{carbon?.symbol}</span>
        }
        readonly
      />
      {!bundleInfoLoading && (
        <div className='mb-2'>
          <p className='modal-description'>
            {t('info.depositFee', {
              fee: carbonBundleInfo.depositFee?.toString(),
            })}
          </p>
        </div>
      )}
      <TransactionButton
        className='w-full px-2'
        loading={txButtonLoading}
        disabled={txButtonDisabled}
        transaction={tx}
        onSuccess={handleSuccess}
        requireTier2={false}
      >
        {txButtonCaption}
      </TransactionButton>
    </div>
  )
}

export default CarbonBundleDeposit
